import React, { useEffect, useMemo, useState } from 'react';
import { Search, TrendingUp, Receipt, Banknote, BadgeCheck, FileCheck2 } from 'lucide-react';
import StatsCard from './analytics/StatsCard';
import RevenueTrendChart from './analytics/RevenueTrendChart';
import TopPerformingAgentsChart from './analytics/TopPerformingAgentsChart';
import CommissionBreakdownChart from './analytics/CommissionBreakdownChart';
import {
  fetchAgentTransactionsAnalytics,
  formatCurrency,
} from '../services/adminAnalyticsApi';
import '../styles/analytics-theme.css';

const PAGE_SIZES = [10, 25, 50, 100];

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' });
}

function PaymentStatus({ value }) {
  const normalized = String(value || '').toLowerCase();
  const isComplete = ['complete', 'completed', 'paid', 'success', 'successful', 'active'].includes(normalized);
  const isPending = ['pending', 'processing', 'initiated'].includes(normalized);
  const isFailed = ['failed', 'error', 'declined', 'cancelled', 'canceled'].includes(normalized);
  const label = normalized === 'active' ? 'Paid' : (value || 'unknown');
  let cls = 'muted';
  if (isComplete) cls = 'success';
  else if (isPending) cls = 'warning';
  else if (isFailed) cls = 'danger';
  return <span className={`ab-badge ${cls}`}>{label}</span>;
}

const AgentsAnalytics = ({ onShowNotification }) => {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [data, setData] = useState({ rows: [], total: 0, stats: {}, charts: {} });

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setFetching(true);
      try {
        const result = await fetchAgentTransactionsAnalytics({ search, page: currentPage, pageSize });
        if (!cancelled) setData(result);
      } catch (error) {
        if (!cancelled && onShowNotification) onShowNotification(error.message, 'error');
      } finally {
        if (!cancelled) {
          setLoading(false);
          setFetching(false);
        }
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, currentPage, pageSize]);

  const stats = data.stats || {};
  const charts = data.charts || {};

  const monthlyTrend = useMemo(
    () => (charts.monthly || []).map((row) => ({
      month: row.month,
      premium: Number(row.gross) || 0,
      commission: Number(row.net) || 0,
      count: Number(row.count) || 0,
    })),
    [charts.monthly]
  );

  const topAgents = useMemo(
    () => (charts.topAgents || []).map((row) => ({
      name: row.name,
      premium: Number(row.net_commission) || 0,
      count: Number(row.count) || 0,
    })),
    [charts.topAgents]
  );

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / pageSize));
  const current = Math.min(currentPage, totalPages);
  const start = data.total === 0 ? 0 : (current - 1) * pageSize + 1;
  const end = Math.min(current * pageSize, data.total || 0);

  if (loading) {
    return (
      <div className="ab-page">
        <div className="ab-empty">Loading agent transactions…</div>
      </div>
    );
  }

  return (
    <div className="ab-page">
      <div className="ab-page-header">
        <div>
          <h1 className="ab-page-title">Agents</h1>
          <p className="ab-page-subtitle">Review agent business transactions, commissions, and certificates</p>
        </div>
        {fetching && <span className="ab-refresh-badge">Refreshing data…</span>}
      </div>

      <div className="ab-search-wrap" style={{ marginBottom: '1.5rem' }}>
        <Search className="ab-search-icon" size={16} />
        <input
          className="ab-search-input"
          placeholder="Search agent, policy, client ID…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
      </div>

      <div className="ab-stats-grid ab-stats-grid-5">
        <StatsCard title="Gross Commission" value={formatCurrency(stats.gross_commission)} subtitle="before withholding tax" icon={TrendingUp} variant="primary" />
        <StatsCard title="Tax (WHT)" value={formatCurrency(stats.withholding_tax)} subtitle="withheld from commissions" icon={Receipt} />
        <StatsCard title="Net Commission" value={formatCurrency(stats.net_commission)} subtitle="payable after tax" icon={Banknote} />
        <StatsCard title="Commissionable Sales" value={Number(stats.commissionable_transactions || 0).toLocaleString()} subtitle="transactions earning commission" icon={BadgeCheck} />
        <StatsCard title="Certificates Issued" value={Number(stats.certificates_issued || 0).toLocaleString()} subtitle="records with certificates" icon={FileCheck2} />
      </div>

      <div className="ab-charts-grid" style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }}>
        <RevenueTrendChart trendData={monthlyTrend} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <CommissionBreakdownChart
            gross={stats.gross_commission}
            tax={stats.withholding_tax}
            net={stats.net_commission}
          />
          <TopPerformingAgentsChart
            topAgents={topAgents}
            title="Top Agents by Net Commission"
            subtitle="Highest net commission earners for current filters"
          />
        </div>
      </div>

      <div className="ab-card">
        <div className="ab-card-header">
          <h3 className="ab-card-title">Agent Business Transactions</h3>
        </div>
        <div className="ab-card-body-flush">
          <div className="ab-table-wrap">
            <table className="ab-table" style={{ minWidth: 1200 }}>
              <thead>
                <tr>
                  <th>Transaction Date</th>
                  <th>Client / Agent</th>
                  <th>Vehicle Reg.</th>
                  <th>Policy Number</th>
                  <th>Cover Type</th>
                  <th>Vehicle Class</th>
                  <th style={{ textAlign: 'right' }}>Premium</th>
                  <th className="highlight-primary" style={{ textAlign: 'right' }}>Gross Commission</th>
                  <th className="highlight-destructive" style={{ textAlign: 'right' }}>Tax (WHT)</th>
                  <th className="highlight-emerald" style={{ textAlign: 'right' }}>Net Commission</th>
                  <th>Payment Status</th>
                  <th>Certificate</th>
                </tr>
              </thead>
              <tbody>
                {(data.rows || []).map((row, idx) => (
                  <tr key={`${row.id || row.policy_number}-${idx}`}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{formatDate(row.transaction_date)}</div>
                      {row.start_date && (
                        <div style={{ fontSize: '0.6875rem', color: 'var(--ab-muted-fg)' }}>
                          Cover: {formatDate(row.start_date)}
                          {row.end_date ? ` → ${formatDate(row.end_date)}` : ''}
                        </div>
                      )}
                    </td>
                    <td>{row.client_name || '—'}</td>
                    <td>{row.vehicle_registration || '—'}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{row.policy_number || '—'}</td>
                    <td>{row.cover_type || '—'}</td>
                    <td>{row.vehicle_class || '—'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 500 }}>{formatCurrency(row.premium_amount)}</td>
                    <td className="cell-primary" style={{ textAlign: 'right' }}>{formatCurrency(row.commission_amount)}</td>
                    <td className="cell-destructive" style={{ textAlign: 'right' }}>{formatCurrency(row.withholding_tax_amount)}</td>
                    <td className="cell-emerald" style={{ textAlign: 'right' }}>{formatCurrency(row.net_commission)}</td>
                    <td><PaymentStatus value={row.payment_status} /></td>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{row.certificate_number || '—'}</td>
                  </tr>
                ))}
                {!data.rows?.length && (
                  <tr>
                    <td colSpan={12} className="ab-empty">No agent transactions match the current search.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="ab-pagination">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.75rem', color: 'var(--ab-muted-fg)' }}>Rows per page</span>
              <select className="ab-select" value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}>
                {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <p style={{ fontSize: '0.75rem', color: 'var(--ab-muted-fg)', margin: 0 }}>
              Showing {start}-{end} of {(data.total || 0).toLocaleString()} transactions
            </p>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="button" className="ab-btn sm" disabled={current === 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>Prev</button>
              <span style={{ fontSize: '0.75rem', color: 'var(--ab-muted-fg)', minWidth: 80, textAlign: 'center', lineHeight: '2rem' }}>Page {current} of {totalPages}</span>
              <button type="button" className="ab-btn sm" disabled={current === totalPages} onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}>Next</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentsAnalytics;
